import React, { useRef, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { ArrowLeft, User, Search } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTrainingStore } from "../../store/store";
import { useAuth } from "../../auth/AuthProvider";
import { Trainee, Test } from "../../types/api";
import { useTrainingModule } from "./TrainingModuleContext";

interface TraineeRow {
  id: string;
  name: string;
  preTestScore: string;
  preTestAttendance: "Present" | "Absent";
}

interface CustomUser {
  id: string;
  roles?: string[];
}

const UpcomingSessionDetails = () => {
  const gridRef = useRef<any>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    fetchTrainings,
    getTestByTrainingId,
    trainings,
    tests,
    loading,
    error,
  } = useTrainingStore();
  const { paths } = useTrainingModule();
  const { user } = useAuth() as { user: CustomUser };
  const [training, setTraining] = useState<any | null>(null);
  const [rowData, setRowData] = useState<TraineeRow[]>([]);
  const [showTraineeModal, setShowTraineeModal] = useState(false);
  const [selectedTestType, setSelectedTestType] = useState<
    "Pre-Test" | "Post-Test" | ""
  >(""); // State for dropdown
  const [traineeSearch, setTraineeSearch] = useState("");

  const userRoles = user?.roles || [];
  const isTrainer =
    userRoles.includes("orgadmin") || userRoles.includes("orguser");

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Legacy trainings only ever persisted total minutes, so synthesize a
  // range from a default 09:00 start instead of showing a bare minute count.
  const DEFAULT_TRAINING_START_TIME = "09:00";
  const formatTrainingTime = (duration: string | undefined): string => {
    const total = parseInt(duration || "0", 10) || 0;
    if (!total) return "-";
    const [h, m] = DEFAULT_TRAINING_START_TIME.split(":").map(Number);
    const endMinutes = (((h * 60 + m + total) % 1440) + 1440) % 1440;
    const endTime = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(
      endMinutes % 60,
    ).padStart(2, "0")}`;
    return `${DEFAULT_TRAINING_START_TIME}-${endTime}`;
  };

  const formatScore = (score: number | undefined): string => {
    return score !== undefined ? `${Math.round(score)}%` : "-";
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        await fetchTrainings({ where: { id } });
        await getTestByTrainingId(id);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, [id, fetchTrainings, getTestByTrainingId]);

  useEffect(() => {
    const selectedTraining = trainings.find((t) => t.id === id);
    if (selectedTraining) {
      setTraining(selectedTraining);

      const preTest = tests.find(
        (test) => test.trainingId === id && test.testCategory === "Pre-Test",
      );

      const traineesData: TraineeRow[] = selectedTraining.trainees.map(
        (trainee: Trainee) => {
          const preTestResult = preTest?.results?.find(
            (result) => result.traineeId === trainee.traineeId,
          );
          return {
            id: trainee.traineeEmployeeId || "-",
            name: trainee.name,
            preTestScore: formatScore(preTestResult?.score),
            preTestAttendance: trainee.attendance?.["Pre-Test"] || "Absent",
          };
        },
      );
      setRowData(traineesData);
    }
  }, [trainings, tests, id]);

  const columnDefs: ColDef[] = [
    { headerName: "ID", field: "id", flex: 1 },
    { headerName: "Name", field: "name", flex: 1 },
    { headerName: "Pre-Test Score", field: "preTestScore", flex: 1 },
    {
      headerName: "Pre-Test Attendance",
      field: "preTestAttendance",
      flex: 1,
      cellRenderer: (params: any) => (
        <div>
          {params.value === "Present" ? (
            <span className="text-green-500">✔</span>
          ) : (
            <span className="text-red-500">✘</span>
          )}
        </div>
      ),
    },
  ];

  const calculateAttendance = (trainees: Trainee[]) => {
    if (!trainees || trainees.length === 0) return "0/0";
    const presentCount = trainees.filter(
      (trainee) => trainee.attendance?.["Pre-Test"] === "Present",
    ).length;
    return `${presentCount}/${trainees.length}`;
  };

  // Handle trainee selection and navigation to AttemptTest
  const handleTraineeSelect = (traineeId: string) => {
    if (!selectedTestType) {
      alert("Please select a test type (Pre-Test or Post-Test).");
      return;
    }
    const selectedTest = tests.find(
      (test) =>
        test.trainingId === id && test.testCategory === selectedTestType,
    );
    if (selectedTest) {
      navigate(
        `${paths.attemptTest(selectedTest.id)}?traineeId=${traineeId}`,
      );
      setShowTraineeModal(false);
    } else {
      alert(`No ${selectedTestType} found for this training.`);
    }
  };

  // Available tests for the dropdown
  const availableTests = tests.filter(
    (test) =>
      test.trainingId === id &&
      ["Pre-Test", "Post-Test"].includes(test.testCategory),
  );

  return (
    <div className="px-2 py-4 space-y-6 mt-14">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold">Upcoming Session Details</h2>
        <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          {isTrainer && (
            <button
              onClick={() => setShowTraineeModal(true)}
              className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-blue-500 text-white hover:bg-blue-600 whitespace-nowrap"
            >
              <User className="mr-2 h-4 w-4" />
              Submit Offline Answers
            </button>
          )}
          <Link to={paths.upcomingSessions}>
            <button className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white whitespace-nowrap">
              <ArrowLeft className="mr-2 h-4 w-4" />
              BACK
            </button>
          </Link>
        </div>
      </div>

      {/* Trainee Selection Modal */}
      {showTraineeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Select Trainee and Test
              </h3>
              <div>
                <label
                  htmlFor="testType"
                  className="block text-sm font-medium text-gray-700"
                >
                  Select Test Type
                </label>
                <select
                  id="testType"
                  value={selectedTestType}
                  onChange={(e) =>
                    setSelectedTestType(
                      e.target.value as "Pre-Test" | "Post-Test" | "",
                    )
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">Select a test</option>
                  {availableTests.length > 0 ? (
                    availableTests.map((test) => (
                      <option key={test.id} value={test.testCategory}>
                        {test.testCategory}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No tests available
                    </option>
                  )}
                </select>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Select Trainee
                </h4>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search trainees"
                    value={traineeSearch}
                    onChange={(e) => setTraineeSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border-2 border-[#a5b7cf] rounded-full text-sm focus:outline-none"
                  />
                </div>
                <ul className="max-h-60 overflow-y-auto space-y-1">
                  {training?.trainees.length > 0 ? (
                    training.trainees
                      .filter((trainee: Trainee) => {
                        const query = traineeSearch.trim().toLowerCase();
                        if (!query) return true;
                        return (
                          trainee.name?.toLowerCase().includes(query) ||
                          trainee.traineeEmployeeId
                            ?.toLowerCase()
                            .includes(query)
                        );
                      })
                      .map((trainee: Trainee) => (
                        <li
                          key={trainee.traineeId}
                          onClick={() => handleTraineeSelect(trainee.traineeId)}
                          className={`p-2 rounded-lg hover:bg-gray-100 cursor-pointer ${
                            !selectedTestType
                              ? "pointer-events-none opacity-50"
                              : ""
                          }`}
                        >
                          {trainee.name}
                          {trainee.traineeEmployeeId
                            ? ` (${trainee.traineeEmployeeId})`
                            : ""}
                        </li>
                      ))
                  ) : (
                    <li className="p-2 text-gray-500">
                      No trainees available
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-4 bg-[#5a687c] px-6 py-4">
              <button
                onClick={() => {
                  setShowTraineeModal(false);
                  setTraineeSearch("");
                }}
                className="px-6 py-2 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : training ? (
        <div className="flex justify-between items-center gap-4 mb-4 bg-white px-4 py-3 shadow-xl">
          <div>
            <p className="text-lg text-black font-bold">Topic</p>
            <p className="font-medium text-black">{training.topic}</p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Category</p>
            <p className="font-medium text-black">{training.category}</p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Date</p>
            <p className="font-medium text-black">
              {formatDate(training.date)}
            </p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Training Time</p>
            <p className="font-medium text-black">
              {training.startTime && training.endTime
                ? `${training.startTime}-${training.endTime}`
                : formatTrainingTime(training.duration)}
            </p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Pre-Test Attendance</p>
            <p className="font-medium text-black">
              {calculateAttendance(training.trainees)}
            </p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Total Trainees</p>
            <p className="font-medium text-black">{training.trainees.length}</p>
          </div>
        </div>
      ) : (
        <div>No training data found</div>
      )}

      <div className="ag-theme-alpine">
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          rowHeight={50}
          headerHeight={65}
          paginationPageSize={20}
          pagination={true}
          domLayout="autoHeight"
          defaultColDef={{
            sortable: true,
            resizable: true,
          }}
          getRowStyle={() => ({
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            marginBottom: "8px",
            backgroundColor: "white",
          })}
        />
      </div>
    </div>
  );
};

export default UpcomingSessionDetails;
